import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconBook, IconDeviceDesktop, IconPlus, IconSearch } from "@tabler/icons-react"
import { useState } from "react"

interface Tag {
  id: number
  name: string
  count: number
}

interface Book {
  id: number
  title: string
  author: string
  book_type: string
  description: string | null
  year_published: number | null
  pages: number | null
  size_in_mb: number | null
  tags: { id: number; name: string }[]
  available_for_checkout: boolean
  currently_checked_out: boolean
  has_pdf: boolean
  owner_name: string
  can_update: boolean
}

interface Props {
  [key: string]: unknown
  books: Book[]
  pagy: PagyMeta
  tags: Tag[]
  type_counts: {
    all: number
    physical: number
    digital: number
  }
  availability_counts: {
    all: number
    available: number
    checked_out: number
  }
  filters: {
    search?: string
    book_type?: string
    tag_id?: string
    availability?: string
  }
  can_create: boolean
}

function truncate(str: string, length: number) {
  if (str.length <= length) return str
  return `${str.slice(0, length)}...`
}

export default function BooksIndex() {
  const { books, pagy, tags, type_counts, filters, can_create } = usePage<Props>().props
  const [search, setSearch] = useState(filters.search || "")

  function applyFilters(newFilters: Record<string, string | undefined>) {
    const merged = { ...filters, ...newFilters }
    const params: Record<string, string> = {}
    Object.entries(merged).forEach(([k, v]) => {
      if (v && v !== "" && v !== "all") params[k] = v
    })
    router.get("/books", params, { preserveState: true })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({ search, page: undefined })
  }

  return (
    <>
      <Head title="Library - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Library</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold tracking-tight">Library</h1>
            {can_create && (
              <Button size="sm" asChild>
                <a href="/books/new">
                  <IconPlus className="mr-2 h-4 w-4" />
                  Add Book
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <form onSubmit={handleSearch} className="relative flex-1 max-w-sm">
            <IconSearch className="text-muted-foreground absolute left-2.5 top-2.5 h-4 w-4" />
            <Input
              placeholder="Search by title or author..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </form>

          <Select
            value={filters.book_type || "all"}
            onValueChange={(v) => applyFilters({ book_type: v === "all" ? undefined : v, page: undefined })}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types ({type_counts.all})</SelectItem>
              <SelectItem value="physical">Physical ({type_counts.physical})</SelectItem>
              <SelectItem value="digital">Digital ({type_counts.digital})</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.availability || "all"}
            onValueChange={(v) => applyFilters({ availability: v === "all" ? undefined : v, page: undefined })}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Availability</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="checked_out">Checked Out</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <button
                type="button"
                key={tag.id}
                onClick={() => applyFilters({ tag_id: String(tag.id), page: undefined })}
              >
                <Badge
                  variant={String(tag.id) === filters.tag_id ? "default" : "secondary"}
                  className="cursor-pointer hover:bg-secondary/80"
                >
                  {tag.name} ({tag.count})
                </Badge>
              </button>
            ))}
            {filters.tag_id && (
              <button type="button" onClick={() => applyFilters({ tag_id: undefined, page: undefined })}>
                <Badge variant="destructive" className="cursor-pointer">
                  Clear Tag Filter
                </Badge>
              </button>
            )}
          </div>
        )}

        {/* Books Grid */}
        {books.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {books.map((book) => (
              <a key={book.id} href={`/books/${book.id}`} className="block">
                <Card className="h-full transition-colors hover:bg-muted/50">
                  <CardContent>
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <h3 className="text-base font-bold leading-tight">{truncate(book.title, 50)}</h3>
                      {book.book_type === "digital" ? (
                        <Badge variant="secondary" className="shrink-0">
                          <IconDeviceDesktop className="mr-1 h-3 w-3" />
                          Digital
                        </Badge>
                      ) : book.available_for_checkout ? (
                        <Badge variant="default" className="shrink-0">
                          Available
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="shrink-0">
                          Checked Out
                        </Badge>
                      )}
                    </div>

                    <p className="mb-3 text-sm text-muted-foreground">by {book.author}</p>

                    {book.description && (
                      <p className="mb-3 text-sm text-muted-foreground">{truncate(book.description, 100)}</p>
                    )}

                    {book.tags.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-1">
                        {book.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag.id} variant="outline" className="text-xs">
                            {tag.name}
                          </Badge>
                        ))}
                        {book.tags.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{book.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {book.book_type === "digital" ? `${book.size_in_mb || "?"} MB` : "Physical Book"}
                        {book.pages && ` \u00B7 ${book.pages} pages`}
                      </span>
                      <span>by {book.owner_name}</span>
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <IconBook className="mb-4 h-16 w-16 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium">No books found</h3>
            <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        )}

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

BooksIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
